import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { isGameMaster } from '../lib/utils';
import { toast } from 'sonner';
import {
  Settings,
  Loader2,
  Gamepad2,
  Download,
  FileText,
  Trash2,
  AlertTriangle,
  Link2,
  ChevronLeft,
} from 'lucide-react';

const API_URL = '/api';

export default function CampaignSettingsPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign, updateCampaign } = useCampaign();

  const [systemInfo, setSystemInfo] = useState(null);
  const [gameSystems, setGameSystems] = useState([]);
  const [selectedSystemId, setSelectedSystemId] = useState('');
  const [savingSystem, setSavingSystem] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);
  const linkedTemplateId = currentCampaign?.linked_template_id;

  useEffect(() => {
    if (currentCampaign?.system_id) {
      axios.get(`/api/game-systems/${currentCampaign.system_id}`)
        .then(r => setSystemInfo(r.data))
        .catch(() => setSystemInfo(null));
    } else {
      setSystemInfo(null);
    }
    setSelectedSystemId(currentCampaign?.system_id || '');
  }, [currentCampaign?.system_id]);

  useEffect(() => {
    if (isMJ) {
      axios.get('/api/game-systems').then(r => setGameSystems(r.data)).catch(() => {});
    }
  }, [isMJ]);

  const handleSaveSystem = async () => {
    setSavingSystem(true);
    try {
      await updateCampaign(campaignId, { system_id: selectedSystemId || null });
      toast.success(t('success_saved', language));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSavingSystem(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/campaigns/${campaignId}/export`, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'];
      let filename = `campaign_${campaignId}.json`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Campagne exportée' : 'Campaign exported');
    } catch {
      toast.error(language === 'fr' ? "Erreur lors de l'export" : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API_URL}/campaigns/${campaignId}/export-pdf`, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'];
      let filename = `campaign_${campaignId}.pdf`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'PDF exporté' : 'PDF exported');
    } catch {
      toast.error(language === 'fr' ? "Erreur lors de l'export PDF" : 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (deleteConfirmName !== currentCampaign?.name) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/campaigns/${campaignId}`);
      toast.success(t('campaign_delete_success', language));
      setDeleteOpen(false);
      navigate('/campaigns');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la suppression' : 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  if (!currentCampaign) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!isMJ) {
    return (
      <MainLayout>
        <div className="text-center py-16 text-muted-foreground">
          {language === 'fr' ? 'Accès réservé au Maître du Jeu.' : 'Access restricted to the Game Master.'}
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="campaign-settings-page">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate(`/campaigns/${campaignId}`)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 group"
          >
            <ChevronLeft className="h-3.5 w-3.5 group-hover:-translate-x-1 transition-transform" />
            {language === 'fr' ? 'Retour au dashboard' : 'Back to dashboard'}
          </button>
          <h1 className="font-cinzel text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            {language === 'fr' ? 'Paramètres de la campagne' : 'Campaign Settings'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{currentCampaign.name}</p>
        </div>

        {/* Game System */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Gamepad2 className="h-4 w-4" />
              {language === 'fr' ? 'Système de jeu' : 'Game System'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Associez un système de jeu à cette campagne pour activer les fiches de personnage et les mécaniques spécifiques.'
                : 'Associate a game system to enable character sheets and system-specific mechanics.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {systemInfo && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                  <Gamepad2 className="h-3 w-3" />
                  {systemInfo.name}
                  {systemInfo.version && ` ${systemInfo.version}`}
                </Badge>
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
              <div className="flex-1 w-full">
                <Label htmlFor="system-select" className="text-sm mb-1.5 block">
                  {t('campaign_system_select', language)}
                </Label>
                <select
                  id="system-select"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={selectedSystemId}
                  onChange={e => setSelectedSystemId(e.target.value)}
                >
                  <option value="">{t('campaign_system_none', language)}</option>
                  {gameSystems.map(s => (
                    <option key={s.id} value={s.id}>{s.name}{s.version ? ` (${s.version})` : ''}</option>
                  ))}
                </select>
              </div>
              <Button onClick={handleSaveSystem} disabled={savingSystem} size="sm">
                {savingSystem && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('save', language)}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Download className="h-4 w-4" />
              {language === 'fr' ? 'Exporter la campagne' : 'Export Campaign'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Téléchargez une copie complète de votre campagne au format JSON (réimportable) ou PDF (lecture seule).'
                : 'Download a full copy of your campaign as JSON (re-importable) or PDF (read-only).'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                {language === 'fr' ? 'Exporter JSON' : 'Export JSON'}
              </Button>
              <Button variant="outline" onClick={handleExportPdf} disabled={exportingPdf}>
                {exportingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                {language === 'fr' ? 'Exporter PDF' : 'Export PDF'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              {language === 'fr' ? 'Zone de danger' : 'Danger Zone'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Ces actions sont irréversibles. Procédez avec prudence.'
                : 'These actions are irreversible. Proceed with caution.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {userRole === 'owner' ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {language === 'fr'
                    ? 'La suppression effacera définitivement la campagne et toutes ses données (sessions, quêtes, PNJ, lieux, cartes...).'
                    : 'Deleting will permanently erase the campaign and all its data (sessions, quests, NPCs, locations, maps...).'}
                </p>
                {linkedTemplateId && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-info/10 text-info text-xs">
                    <Link2 className="h-4 w-4 shrink-0" />
                    {t('campaign_delete_confirm_marketplace', language)}
                  </div>
                )}
                <Button
                  variant="destructive"
                  onClick={() => { setDeleteOpen(true); setDeleteConfirmName(''); }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('campaign_delete', language)}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {language === 'fr'
                  ? 'Seul le propriétaire de la campagne peut la supprimer.'
                  : 'Only the campaign owner can delete it.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {t('campaign_delete_confirm_title', language)}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p className="font-medium text-foreground">
                {t('campaign_delete_confirm_warning', language)}
              </p>
              <p className="text-xs text-muted-foreground italic">
                {t('campaign_delete_confirm_list', language)}
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2">
            <Label className="text-sm">
              {t('campaign_delete_confirm_input', language)}
            </Label>
            <p className="text-xs font-mono font-semibold text-foreground bg-muted px-2 py-1 rounded">
              {currentCampaign?.name}
            </p>
            <Input
              value={deleteConfirmName}
              onChange={e => setDeleteConfirmName(e.target.value)}
              placeholder={currentCampaign?.name}
              className="border-destructive/30"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              {t('cancel', language)}
            </Button>
            <Button
              variant="destructive"
              disabled={deleting || deleteConfirmName !== currentCampaign?.name}
              onClick={handleDeleteCampaign}
            >
              {deleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {deleting ? t('campaign_deleting', language) : t('campaign_delete', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
